import React, {
  memo,
  ReactElement,
  useCallback,
  useState,
  useEffect,
} from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { useFormState } from '../Form/Form'
import Modal, { ModalCloseButton, ModalDisplayTypes } from '../Modal/Modal'
import ContactUsFormModalValidation from './ContactUsFormModal.validation'
import './ContactUsFormModal.scss'
import { addFormValidation } from '../Validators/InputValidations'

type ContactUsFormModalPropTypes = {
  submitApi?: (values: FormData) => Promise<{}>
  propertySlug?: string
} & ModalDisplayTypes

type ProjectListTypes = {
  label: string
  value: string
  slug: string
}

const ContactUsFormModal = ({
  isShowing,
  propertySlug,
  onClose,
  submitApi,
}: ContactUsFormModalPropTypes): ReactElement => {
  const {
    allProperty: { nodes: projectOptionList },
  }: {
    allProperty: {
      nodes: ProjectListTypes[]
    }
  } = useStaticQuery(
    graphql`
      query ProjectOptionList {
        allProperty {
          nodes {
            label: projectName
            value: projectName
            slug
          }
        }
      }
    `,
  )
  const { values, validateFields } = useFormState({
    validation: ContactUsFormModalValidation,
  })
  const [isSuccessShown, setIsSuccessShown] = useState<boolean>(false)
  const [isAgreeChecked, setIsAgreeChecked] = useState<boolean>(false)
  const [isRecaptchaChecked, setIsRecaptchaChecked] = useState<boolean>(false)
  const [defaultProperty] = useState<ProjectListTypes | undefined>(
    projectOptionList
      ? projectOptionList.find((project) => project.slug === propertySlug)
      : undefined,
  )
  const handleSubmit = useCallback(async () => {
    if (isAgreeChecked && isRecaptchaChecked) {
      const fieldErrors = validateFields(values)

      if (!Object.keys(fieldErrors).length) {
        try {
          if (submitApi) {
            const formData = new FormData()
            Object.entries(values).forEach(([key, value]) => {
              formData.append(key, value)
            })

            await submitApi(formData)
          }

          setIsSuccessShown(true)
        } catch (e) {
          setIsSuccessShown(false)
        }
      }
    }
  }, [isAgreeChecked, isRecaptchaChecked, submitApi, validateFields, values])

  const setPropertyName = useCallback(async (options, property) => {
    const setSelected = Array.from(options)?.forEach((el: any, i): any => {
      if (el.value === property.value) {
        const insertValue = document
          .getElementById('contact-form')
          ?.getElementsByTagName('select')
          .item(0)
          ?.getElementsByTagName('option')
          .item(i)
          ?.setAttribute('selected', 'true')
      }
    })
  }, [])

  const inputProperty = useCallback(async () => {
    window.addEventListener('message', (event) => {
      if (
        event.data.type === 'hsFormCallback' &&
        event.data.eventName === 'onFormReady'
      ) {
        const options = document
          .getElementById('contact-form')
          ?.getElementsByTagName('select')
          .item(0)
          ?.getElementsByTagName('option')
        if (defaultProperty && options) {
          const found = Array.from(options).filter(
            (el) => el.value === defaultProperty.value,
          )
          if (found.length > 0) {
            setPropertyName(options, defaultProperty)
          } else {
            const setDefault = document
              .getElementById('contact-form')
              ?.getElementsByTagName('select')
              .item(0)
            if (setDefault) setDefault.selectedIndex = 0
          }
        }
      }
    })
  }, [defaultProperty, setPropertyName])

  const handleFormReady = useCallback(() => {
    addFormValidation('contact-form', [
      {
        elementId: 'name-82e35df6-4d1c-4ace-8eae-fdb7f5d084e1',
        validationType: 'alphaOnly',
      },
      {
        elementId: 'phone-82e35df6-4d1c-4ace-8eae-fdb7f5d084e1',
        validationType: 'numericOnly',
      },
      {
        elementId: 'location-82e35df6-4d1c-4ace-8eae-fdb7f5d084e1',
        validationType: 'alphaNumericWithSpecialCharacters',
      },
      {
        elementId: 'message-82e35df6-4d1c-4ace-8eae-fdb7f5d084e1',
        validationType: 'alphaNumericWithSpecialCharacters',
      },
    ])

    // add maxlength in contact number
    const addMaxLengthContacNo: HTMLElement | null = document.getElementById(
      'phone-82e35df6-4d1c-4ace-8eae-fdb7f5d084e1',
    )

    const isElement = (input: HTMLElement | null): input is HTMLElement =>
      input !== null

    if (isElement(addMaxLengthContacNo)) {
      addMaxLengthContacNo.setAttribute('maxLength', '12')
    }
  }, [])

  useEffect(() => {
    if (isShowing && window.hbspt?.forms) {
      window.jQuery =
        window.jQuery ||
        ((): { change(): null; trigger(): null } => ({
          change: (): null => null,
          trigger: (): null => null,
        }))
      window.hbspt.forms.create({
        portalId: '8605476',
        formId: '82e35df6-4d1c-4ace-8eae-fdb7f5d084e1',
        target: '#contact-form',
        onFormReady: handleFormReady,
      })

      inputProperty()
    }
  }, [handleFormReady, inputProperty, isShowing])

  return (
    <Modal
      isShowing={isShowing}
      onClose={onClose}
      displayClose={false}
      side
      noPadding
    >
      <div className="contact-us-form">
        <div className="contact-us-form-header">
          <ModalCloseButton onClose={onClose} />
        </div>
        <div className="contact-us-form-content">
          <h2 className="contact-us-form-title">Contact Us</h2>
          <h4 className="contact-us-form-description">Inquiry Form</h4>
          <div id="contact-form" />
        </div>
      </div>
    </Modal>
  )
}

export default memo(ContactUsFormModal)
